import { z } from "zod";

/** A "stuff" is a saved grenade throw. This milestone covers singles: one throw, one landing. */

export const GRENADES = ["smoke", "flash", "molotov", "he"] as const;
export const Grenade = z.enum(GRENADES);
export type Grenade = z.infer<typeof Grenade>;

export const SIDES = ["t", "ct"] as const;
export const Side = z.enum(SIDES);
export type Side = z.infer<typeof Side>;

export const AREAS = ["a", "b", "mid", "other"] as const;
export const Area = z.enum(AREAS);
export type Area = z.infer<typeof Area>;

export const MOVEMENTS = ["standing", "crouching", "walking", "running"] as const;
export const Movement = z.enum(MOVEMENTS);
export type Movement = z.infer<typeof Movement>;

export const CLICKS = ["left", "right", "both"] as const;
export const Click = z.enum(CLICKS);
export type Click = z.infer<typeof Click>;

/**
 * How much a stuff is played: generic (thrown every round), useful (about half
 * the rounds) or situational (when the round calls for it).
 */
export const IMPORTANCES = ["generic", "useful", "situational"] as const;
export const Importance = z.enum(IMPORTANCES);
export type Importance = z.infer<typeof Importance>;
/** Generic first: the order Live and Learn list stuffs in. */
export const importanceRank = (i: Importance | undefined) => IMPORTANCES.indexOf(i ?? "useful");

export const STATUSES = ["to_learn", "practising", "learned"] as const;
export const LearnStatus = z.enum(STATUSES);
export type LearnStatus = z.infer<typeof LearnStatus>;

/** Seconds on the round clock: a round starts at 1:55. */
export const ROUND_SECONDS = 115;

const finite = z.number().finite();
/** CS2 world coordinates stay within ±16384. */
const coord = finite.min(-16384).max(16384);

export const ThrowSpot = z.object({
  x: coord,
  y: coord,
  z: coord,
  pitch: finite.min(-90).max(90),
  yaw: finite.min(-360).max(360),
});
export type ThrowSpot = z.infer<typeof ThrowSpot>;

export const Landing = z.object({
  x: coord,
  y: coord,
  /** Known when the landing came from a second getpos; unknown when clicked on the map. */
  z: coord.nullable(),
});
export type Landing = z.infer<typeof Landing>;

export const Technique = z.object({
  movement: Movement,
  jump: z.boolean(),
  click: Click,
});
export type Technique = z.infer<typeof Technique>;

/** When the throw is useful, as round-clock times counting down: from 1:45 (105) to 1:30 (90). */
export const UsefulWindow = z
  .object({
    from: z.number().int().min(0).max(ROUND_SECONDS),
    to: z.number().int().min(0).max(ROUND_SECONDS),
    label: z.string().trim().max(40).default(""),
  })
  .refine((w) => w.from >= w.to, { message: "A window starts before it ends: the round clock counts down" });
export type UsefulWindow = z.infer<typeof UsefulWindow>;

export const YouTubeClip = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{11}$/, "Not a YouTube video"),
  start: z.number().int().min(0).nullable(),
  end: z.number().int().min(0).nullable(),
});
export type YouTubeClip = z.infer<typeof YouTubeClip>;

export const StuffInput = z.object({
  map: z.string(),
  grenade: Grenade,
  side: Side,
  area: Area,
  title: z.string().trim().min(2, "At least 2 characters").max(60, "At most 60 characters"),
  throw: ThrowSpot,
  landing: Landing,
  technique: Technique,
  notes: z.string().trim().max(1000).default(""),
  youtube: YouTubeClip.nullable().default(null),
  windows: z.array(UsefulWindow).max(6).default([]),
  importance: Importance.default("useful"),
});
export type StuffInput = z.infer<typeof StuffInput>;

/** Where a stuff lives: a team, or the player's personal library. */
export const Scope = z.union([z.literal("personal"), z.string().uuid()]);
export type Scope = z.infer<typeof Scope>;

export const CreateStuffInput = StuffInput.extend({ scope: Scope });
export type CreateStuffInput = z.infer<typeof CreateStuffInput>;

export const MEDIA_CAPTIONS = ["stand", "aim", "landing", "other"] as const;
export const MediaCaption = z.enum(MEDIA_CAPTIONS);
export type MediaCaption = z.infer<typeof MediaCaption>;

export const MAX_MEDIA_PER_STUFF = 6;
/** After the browser shrinks big screenshots (apps/web/src/lib/shrink-image.ts), files are far below this. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

/** A video the player uploads: MP4 (H.264), WebM or MOV, kept as sent. */
export const MAX_CLIP_BYTES = 150 * 1024 * 1024;
export const CLIP_TYPES = ["video/mp4", "video/webm", "video/quicktime"] as const;

export interface StuffClip {
  url: string;
  contentType: string;
  bytes: number;
}

export interface StuffMedia {
  id: string;
  url: string;
  caption: MediaCaption;
}

export interface Stuff extends StuffInput {
  id: string;
  teamId: string | null;
  author: { id: string; name: string };
  media: StuffMedia[];
  /** The uploaded video, if any (YouTube links are in `youtube`). */
  clip: StuffClip | null;
  myStatus: LearnStatus | null;
  /** Players of the team, by status (personal stuffs: only you). */
  progress: { learned: number; practising: number; members: number };
  canEdit: boolean;
  /** Combos, executions and post-plants using this single (on its own page). */
  usedIn?: { kind: "combo" | "execution" | "plant"; id: string; title: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface MapCount {
  map: string;
  total: number;
  learnedByMe: number;
}

/** Reads a YouTube address (watch, youtu.be, shorts, embed) with its t= start time. */
export function parseYouTube(input: string): { id: string; start: number | null } | null {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\.|^m\./, "");
  let id: string | null = null;
  if (host === "youtu.be") id = url.pathname.slice(1);
  else if (host === "youtube.com" || host === "youtube-nocookie.com") {
    if (url.pathname === "/watch") id = url.searchParams.get("v");
    else {
      const m = /^\/(?:shorts|embed|live)\/([^/?]+)/.exec(url.pathname);
      id = m ? m[1] : null;
    }
  }
  if (!id || !/^[A-Za-z0-9_-]{11}$/.test(id)) return null;
  const t = url.searchParams.get("t") ?? url.searchParams.get("start");
  return { id, start: t ? parseTimestamp(t) : null };
}

/** "95", "95s", "1m35s", "1:35" → 95. */
export function parseTimestamp(value: string): number | null {
  const v = value.trim();
  if (/^\d+$/.test(v)) return Number(v);
  const colon = /^(\d+):([0-5]?\d)$/.exec(v);
  if (colon) return Number(colon[1]) * 60 + Number(colon[2]);
  const hms = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(v);
  if (hms && (hms[1] || hms[2] || hms[3])) return Number(hms[1] ?? 0) * 3600 + Number(hms[2] ?? 0) * 60 + Number(hms[3] ?? 0);
  return null;
}

/** 105 → "1:45". */
export const formatClock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** Copying stuffs into another library (personal or a team): at most this many at once. */
export const MAX_COPY = 50;
export const CopyStuffsInput = z.object({
  ids: z.array(z.string().uuid()).min(1, "Choose stuffs to copy.").max(MAX_COPY),
  to: Scope,
});
export type CopyStuffsInput = z.infer<typeof CopyStuffsInput>;

export interface CopyStuffsResult {
  /** Each copy, with the stuff it came from. */
  copied: { from: string; id: string }[];
  /** Left out: the library already has the same throw, or it is already in that library. */
  skipped: { id: string; reason: "already_there" | "same_library" }[];
  /** Pictures or videos that could not be copied (storage error): the copies exist without them. */
  missingFiles: number;
}

type ThrowShape = { map: string; grenade: string; throw: { x: number; y: number }; landing: { x: number; y: number } };

/** The same throw in two libraries: same grenade, standing on the same spot, landing in about the same place. */
export function sameThrow(a: ThrowShape, b: ThrowShape): boolean {
  return (
    a.map === b.map &&
    a.grenade === b.grenade &&
    Math.abs(a.throw.x - b.throw.x) <= 2 &&
    Math.abs(a.throw.y - b.throw.y) <= 2 &&
    Math.hypot(a.landing.x - b.landing.x, a.landing.y - b.landing.y) <= 48
  );
}
